import {FetchError, myJsonSyncXhrFetcher} from "../../utils/getData.js";
import { translate } from "../../i18n/i18next.js";

const _SemicolonDateToArray = (semicolonSeparatedDate) => {
  if (semicolonSeparatedDate === "") return [];
  return semicolonSeparatedDate.split(";").map((d) => new Date(d));
}

const __getConnectedComponents = (pairs) => {
    const parent = {};
    const rank = {};

    // Find the root of the set in which element `i` belongs
    const find = (i) => {
        if (parent[i] !== i) {
            parent[i] = find(parent[i]); // Path compression
        }
        return parent[i];
    };

    // Perform the union of two subsets
    const union = (i, j) => {
        const rootI = find(i);
        const rootJ = find(j);

        if (rootI !== rootJ) {
            // Union by rank
            if (rank[rootI] > rank[rootJ]) {
                parent[rootJ] = rootI;
            } else if (rank[rootI] < rank[rootJ]) {
                parent[rootI] = rootJ;
            } else {
                parent[rootJ] = rootI;
                rank[rootI] += 1;
            }
        }
    };

    // Initialize the Union-Find structure
    for (const [u, v] of pairs) {
        if (parent[u] === undefined) {
            parent[u] = u;
            rank[u] = 0;
        }
        if (parent[v] === undefined) {
            parent[v] = v;
            rank[v] = 0;
        }
        union(u, v);
    }

    // Find all the connected components
    const components = {};
    for (const node in parent) {
        const root = find(node);
        if (!components[root]) {
            components[root] = [];
        }
        components[root].push(node);
    }

    // Convert the components object into an array of arrays
    return Object.values(components);
};

export const FetchLocs = (url, fetchErrorHandler) => {
  try {
    const data = myJsonSyncXhrFetcher(url)
    return Object.fromEntries(Object.entries(data).map(([k, [v, n]]) => [k, [String(v), n]]))
  } catch (e) {
    if (e instanceof FetchError) {
      fetchErrorHandler(e.error)
    }
    return {}
  }
}

export const PostProcessOperations = (oper) => {
  let retOps = oper;
  let clusterCount = 0;

  for (let i = 0; i < retOps.length; i++) {
    let _o = retOps[i];

    // Full ope duration
    {
      let itemStart = new Date(0);
      let itemEnd = new Date(0);

      // Begin
      if (_o._setupArr.length >= 2) itemStart = _o._setupArr[0];
      else if (_o._productionArr.length >= 2) itemStart = _o._productionArr[0];
      else if (_o._teardownArr.length >= 2) itemStart = _o._teardownArr[0];

      // End
      if (_o._teardownArr.length >= 2) itemEnd = _o._teardownArr[_o._teardownArr.length-1];
      else if (_o._productionArr.length >= 2) itemEnd = _o._productionArr[_o._productionArr.length-1];
      else if (_o._setupArr.length >= 2) itemEnd = _o._setupArr[_o._setupArr.length-1];

      // Output
      retOps[i]._operationStartEnd = [itemStart, itemEnd];
      retOps[i]._influenceStart = itemStart;
      retOps[i]._influenceEnd = itemEnd;
    }

    // For later
    retOps[i]._textEnd = null;
  }

  // Area of influence
  {
    let elementCount = Object.keys(retOps).length;
    let edgeList = [];

    // Populate
    for (let i = 0; i < retOps.length; i++) {
      let _o = retOps[i];

      let startOfInfluence = _o._operationStartEnd[0];
      let endOfInfluence = _o._operationStartEnd[1];
      for (let j = i + 1; j < retOps.length; j++) {
        let _other = retOps[j];

        if (_other._operationStartEnd[0] >= endOfInfluence) continue;
        edgeList.push([i, j]);
        break;
      }
      for (let j = i - 1; j >= 0; j--) {
        let _other = retOps[j];

        if (_other._operationStartEnd[1] <= startOfInfluence) continue;
        edgeList.push([i, j]);
        break;
      }
    }

    const components = __getConnectedComponents(edgeList);
    clusterCount = components.length;

    components.forEach((component, idx) => {
      const startDates = component.map((e) => retOps[e]._operationStartEnd[0]);
      const endDates = component.map((e) => retOps[e]._operationStartEnd[1]);
      const startInfluence = new Date(Math.min(...startDates));
      const endInfluence = new Date(Math.max(...endDates));
      const clusterNo = idx;
      component.forEach((node) => {
        retOps[node]._influenceStart = startInfluence;
        retOps[node]._influenceEnd = endInfluence;
        retOps[node]._clusterNo = clusterNo; // undefined = non-stacked operation
      });
    });
  }

  // 多重資源
  {
    // 1st step: Get the vertical position of each operation checking the previous ones
    for (let i = 0; i < retOps.length; i++) {
      let _o = retOps[i];

      let verticalPosition = 0;
      let startOfInfluence = _o._operationStartEnd[0]; // ! Not _influenceStart as we just want to take into account the places where the block cannot absolutely be placed at its starting time
      let socles = [0];
      let takenRanges = [];
      for (let j = i - 1; j >= 0; j--) {
        let _other = retOps[j];
        if (_other._operationStartEnd[1] <= startOfInfluence) continue;

        socles.push(_other._verticalPosition + _other._takenResourceCount);
        // [A,B[
        takenRanges.push([_other._verticalPosition, _other._verticalPosition + _other._takenResourceCount]);
      }

      // Take the lowest available spot
      socles.sort((a, b) => a - b);
      for (let k = 0; k < socles.length; k++) {
        const socle = socles[k];
        const top = socle + _o._takenResourceCount;

        const collides = takenRanges.some((range) => {
          return (range[1] <= top) ? !(range[1] <= socle) : !(top <= range[0]);
        });

        if (!collides) {
          verticalPosition = socle;
          break;
        }
      }

      // Output
      retOps[i]._verticalPosition = verticalPosition;
    }

    // 2nd step: Set the maximum necessary space for each operation within its sphere of influence

    // 2.1: Determine cluster-size heights
    let clusterHeights = {};

    for (let i = 0; i < retOps.length; i++) {
      let _o = retOps[i];
      const _cluster = _o._clusterNo;

      // Not in any cluster
      if (_cluster === undefined) {
        retOps[i]._necessarySpace = _o._takenResourceCount;
        retOps[i]._clusterNo = -1;
      }
      // In cluster
      else if (_cluster >= 0) {
        if (!(_cluster in clusterHeights)) clusterHeights[_cluster] = 0;
        clusterHeights[_cluster] = Math.max(_o._verticalPosition + _o._takenResourceCount, clusterHeights[_cluster]);
      }
    }

    // 2.2: Apply cluster specific values to each operation
    for (let i = 0; i < retOps.length; i++) {
        let _o = retOps[i];
        const _cluster = _o._clusterNo;
        if (_cluster >= 0) {
            retOps[i]._necessarySpace = clusterHeights[_cluster];
        }

        // Easy fix for the reverse Y-axis issue, not the prettiest but the most efficient without altering too much the code
        retOps[i]._verticalPosition = retOps[i]._necessarySpace - _o._verticalPosition - _o._takenResourceCount;
    }

  }

  // End of text box
  for (let i = 0; i < retOps.length; i++) {
    let _curr = retOps[i];

    let itemStart = new Date(0);
    let itemEnd = new Date(0);
    let nextStart = new Date(0);

    // Curr Begin prod-based
    if (_curr._productionArr.length >= 2) itemStart = _curr._productionArr[0];
    else if (_curr._setupArr.length >= 2) itemStart = _curr._setupArr[0];
    else if (_curr._teardownArr.length >= 2) itemStart = _curr._teardownArr[0];

    // Curr End prod-based
    if (_curr._productionArr.length >= 2) itemEnd = _curr._productionArr[_curr._productionArr.length-1];
    else if (_curr._teardownArr.length >= 2) itemEnd = _curr._teardownArr[_curr._teardownArr.length-1];
    else if (_curr._setupArr.length >= 2) itemEnd = _curr._setupArr[_curr._setupArr.length-1];

    retOps[i]._textStart = itemStart;

    if (i === retOps.length - 1) continue;

    let _next = retOps[i + 1];

    // Next End setup-based
    if (_next._setupArr.length >= 2) nextStart = _next._setupArr[0];
    else if (_next._productionArr.length >= 2) nextStart = _next._productionArr[0];
    else if (_next._teardownArr.length >= 2) nextStart = _next._teardownArr[0];

    // Not in any cluster
    if (_curr._clusterNo === -1 && _next._productionArr.length > 0) {
      retOps[i]._textEnd = nextStart;
    }
    else if (_curr._productionArr.length > 0) {
      retOps[i]._textEnd = itemEnd;
    }


  }

  return oper;
}

export const PostProcessShifts = (shifts, ocalendar) => {
  if (ocalendar.length === 0) return shifts;
  
  const subtractTimeRange = (baseRange, subtractRange) => {
    const [bStart, bEnd] = baseRange.map(d => d.getTime());
    const [sStart, sEnd] = subtractRange.map(d => d.getTime());
  
    if (sEnd <= bStart || sStart >= bEnd) return [baseRange];
  
    const result = [];
  
    if (sStart > bStart) result.push([new Date(bStart), new Date(sStart)]);
    if (sEnd < bEnd) result.push([new Date(sEnd), new Date(bEnd)]);
  
    return result;
  }

  let results = [];

  shifts.forEach((shift) => {
    let segments = [shift];

    ocalendar.forEach((ocal) => {
      const ts = ocal.TimeSeries;

      const newSegments = [];
      segments.forEach((seg) => {
        newSegments.push(...subtractTimeRange(seg, ts));
      });
      segments = newSegments;
    });

    results.push(...segments);
  });

  const sortedOcalendars = ocalendar
    .map((item, index) => ({ ...item, _originalIndex: index }))
    .sort((a, b) => {
      const aTime = new Date(a.Timestamp).getTime();
      const bTime = new Date(b.Timestamp).getTime();

      // 1. Sort by Timestamp
      if (aTime !== bTime) return aTime - bTime;
      
      // 2. Sort by SortOrder
      const aHasOrder = a.SortOrder != null;
      const bHasOrder = b.SortOrder != null;

      // 2-1. SortOrder present for both -> Lower goes first
      if (aHasOrder && bHasOrder) return a.SortOrder - b.SortOrder;

      // 2-2. No SortOrder in one of the elements -> No sort order goes first
      if (aHasOrder) return 1;
      if (bHasOrder) return -1;

      // 2-3. No SortOrder -> Preserve base order
      return a._originalIndex - b._originalIndex;
    })
    .map(({ _originalIndex, ...item }) => item); // Remove temporary base index

  let OcalShifts = [];

  sortedOcalendars.forEach((ocal) => {
    if (ocal.IsActiveShift) {
      OcalShifts.push(ocal.TimeSeries);
    }
    else {
      const _tmpCut = [];

      OcalShifts.forEach((shift) => {
        let segments = [shift];
    
        const ts = ocal.TimeSeries;
    
        const newSegments = [];
        segments.forEach((seg) => {
          newSegments.push(...subtractTimeRange(seg, ts));
        });
        segments = newSegments;
    
        _tmpCut.push(...segments);
      });

      OcalShifts = [...OcalShifts, ..._tmpCut];
    }
  });

  results = [...results, ...OcalShifts];

  return results;
}

export const FetchResourceInfo = (res, url, fetchErrorHandler) => {
  const _resUrl = url;//this.rowDownloadUrl + urlIdx + ".json";
  let data
  try {
    data = myJsonSyncXhrFetcher(_resUrl)
  } catch (e) {
    console.error("Failed to fetch : " + res)
    if (e instanceof FetchError) {
      fetchErrorHandler(e.error)
    }
    return {
      Data: [],
      Shift: [],
      Label: [],
      OCalendar: [],
      IntervalQty: [],
    }
  }

  const GenerateOCalendarDataTip = (timeArray, dispString, resQty) => {
    const diffStr = (start, end) => { 
      let d = Math.floor((end - start) / 1000),
        s = d % 60,
        m = Math.floor(d / 60) % 60,
        h = Math.floor(d / 3600) % 24,
        D = Math.floor(d / 86400); 
      return `${D?D+'D':''}${h?h+'h':''}${m?m+'m':''}${s?s+'s':''}`; 
    };

    if (timeArray.length < 2) return "";

    let ret = `${translate("frontend:ganttChart.ocalendar.duration")}: ${diffStr(timeArray[0],timeArray[1])}
${translate("frontend:ganttChart.ocalendar.resQty")}: ${resQty}`;

    if (dispString !== "" && dispString !== undefined) ret += `
${translate("frontend:ganttChart.ocalendar.displayString")}: ${dispString}`; 

    return ret;
  }

  const BuildShifts = (_legacyWT, _WTStartDate, _WTPatterns, _WTSchedule) => {
    // Use legacy working times if they already exist, prioritized if both exist due to being cheaper to compute
    if (_legacyWT !== undefined) return _legacyWT?.map((el) => [new Date(el[0]), new Date(el[1])]) ?? [];

    // Optimized work times
    const _baseDate = new Date(_WTStartDate);
    const _buildShiftsFromPattern = (_offset, _patternNo) => {
      const _pattern = (_patternNo >= 0 && _patternNo < _WTPatterns.length) ? _WTPatterns[_patternNo] : [];
      // Please stick to the formats I send to you so I don't need to do this kind of weird shenanigans to make it work as it should
      const _adjusted = (([a, b]) => [a, (([h1, m1, s1], [h2, m2, s2]) => { const t1 = h1 * 3600 + m1 * 60 + s1, t2 = h2 * 3600 + m2 * 60 + s2, h = t2 < t1 ? h2 + 24 : h2; return `${String(h).padStart(2, '0')}:${String(m2).padStart(2, '0')}:${String(s2).padStart(2, '0')}` })(a.split(':').map(Number), b.split(':').map(Number))])(_pattern);

      // Stinky triple nested closure but helps to keep this function minimal and simple to manage
      const _addDaysAndTime = (date, n, time) => {
        const [h, m, s] = time.split(':').map(Number);
        const d = new Date(date);
        d.setHours(d.getHours() + n * 24 + h, d.getMinutes() + m, d.getSeconds() + s);
        return d;
      };      
      return _adjusted.map((_time) => _addDaysAndTime(_baseDate, _offset, _time));
    }
    return _WTSchedule.map((_schedule) => _buildShiftsFromPattern(_schedule[0], _schedule[1]));
  }

  let AllOperations = [];
  let AllShifts = BuildShifts(data.WorkingTime, data.WorkingTime_StartDate, data.WorkingTime_Patterns, data.WorkingTime_Schedule);
  let AllLabels = [];
  let AllOCalendar = data.OCalendar?.map((el) => {
    return { 
      Code: el.Code, 
      Color: el.Color,
      SortOrder: el?.SortOrder ?? null,
      Timestamp: (el.Timestamp !== undefined) ? new Date(el.Timestamp) : new Date(),
      DisplayText: el.DisplayText,
      TimeSeries: ((el?.TimeSeries?.length ?? []) > 0) ? [new Date(el.TimeSeries[0]), new Date(el.TimeSeries[el.TimeSeries.length-1])] : [],
      IsActiveShift: ((el?.resQty?.length ?? []) > 0) ? ((el.resQty[0] > 0) ? true : false) : false,
      DataTip: GenerateOCalendarDataTip(
        ((el?.TimeSeries?.length ?? []) > 0) ? [new Date(el.TimeSeries[0]), new Date(el.TimeSeries[el.TimeSeries.length-1])] : [],
        el.DisplayText,
        el?.resQty?.[0] ?? 0
      ),
    }
  }) ?? [];

  //this.registerShifts(res, data.WorkingTime);

  const PDA = Object.keys(data.PropertyDef_Operation);
  const _indexes = {
    ObjectID: PDA.indexOf('ObjectID'),
    OrderCode: PDA.indexOf('WorkInst_Order'),
    OutputItem: PDA.indexOf('Work_OperationOutMainItem'),
    OperationCode: PDA.indexOf('WorkInst_Operation'),
    OutputItemQuantity: PDA.indexOf('Work_OperationOutMainItemQty'),
    Color: PDA.indexOf('WebViewerOnly_Order_Color_RGB'),
    UIMType: PDA.indexOf('WebViewerOnly_UseInstMaster_Type'),
    PreviousTask: PDA.indexOf('WebViewerOnly_LeftWorkObjectID'),
    NextTask: PDA.indexOf('WebViewerOnly_RightWorkObjectID'),
    PreviousTaskLocalLocs: PDA.indexOf('WebViewerOnly_LeftWorkLocalLocs'),
    NextTaskLocalLocs: PDA.indexOf('WebViewerOnly_RightWorkLocalLocs'),
    Lateness: PDA.indexOf('Work_Lateness'),
    Earlyness: PDA.indexOf('Work_Earliness'),
    ProductionArray: PDA.indexOf('WebViewerOnly_TimeArray_Prod'),
    SetupArray: PDA.indexOf('WebViewerOnly_TimeArray_Setup'),
    TeardownArray: PDA.indexOf('WebViewerOnly_TimeArray_Teardown'),
    ResLockArray: PDA.indexOf('WebViewerOnly_TimeArray_ResLock1'),
    Status: PDA.indexOf('Work_Status'),
    TaskText: PDA.indexOf('WebViewerOnly_BarString'),
    TaskTooltip: PDA.indexOf('WebViewerOnly_DataTipString'),
    ResultStartTime: PDA.indexOf('Work_ResultStartTime'),
    ResultEndTime: PDA.indexOf('Work_ResultEndTime'),
    ResultQty: PDA.indexOf('Work_ResultQty'),
    TimeConstraintMethod: PDA.indexOf('WebViewerOnly_MasterInputInst_LeftWorkTimeConstraintMethods'),
    PegType: PDA.indexOf('WebViewerOnly_Peg_LeftWorkPegType'),
    ProcNo: PDA.indexOf('Work_OperationProcNo'),
    ProcName: PDA.indexOf('Work_OperationProcName'),
    ProcCode: PDA.indexOf('UseInst_OperationProcCode'),
    ResultProgress : PDA.indexOf('Work_ResultProgress'),
    ResultObtainTime: PDA.indexOf('Work_ResultObtainTime'),
    OrderType: PDA.indexOf('Order_OrderType'),
    OrderItemCode: PDA.indexOf('Order_Item_Code'),
    OrderItemName: PDA.indexOf('Order_Item_Name'),
    ConnectionLineLeftTimestamp: PDA.indexOf('WebViewerOnly_ConnectionLineLeftTimestamps'),
    ConnectionLineRightTimestamps: PDA.indexOf('WebViewerOnly_ConnectionLineRightTimestamps'),
    UseInstBarTextColor: PDA.indexOf('UseInst_BarTextColor')
  };

  const PDL = Object.keys(data.PropDef_Label);
  const _labelIndexes = {
    ObjectID: PDL.indexOf('ObjectID'),
    LabelString: PDL.indexOf('Label_String'),
    LabelTextHorizontalAlignment: PDL.indexOf('Label_TextHorizontalAlignment'),
    LabelTextVerticalAlignment: PDL.indexOf('Label_TextVerticalAlignment'),
    LabelShape: PDL.indexOf('Label_Shape'),
    LabelTimePoint: PDL.indexOf('Label_TimePoint'),
    LabelTimeSpan: PDL.indexOf('Label_TimeSpan'),
    LabelRelYRatio: PDL.indexOf('Label_RelYRatio'),
    LabelRectRelX: PDL.indexOf('Label_RectRelX'),
    LabelRectRelY: PDL.indexOf('Label_RectRelY'),
    LabelZ: PDL.indexOf('Label_Z'),
    LabelHeight: PDL.indexOf('Label_Height'),
    LabelWidth: PDL.indexOf('Label_Width'),
    LabelArrowDepthRatio: PDL.indexOf('Label_ArrowDepthRatio'),
    LabelArrowStemHeightRatio: PDL.indexOf('Label_ArrowStemHeightRatio'),
    LabelBackColor: PDL.indexOf('Label_BackColor'),
    LabelBorderColor: PDL.indexOf('Label_BorderColor'),
    LabelTextColor: PDL.indexOf('Label_TextColor'),
    MyScheduleOnlyLabelResourceCode: PDL.indexOf('MyScheduleOnly_Label_ResourceCode'),
    MyScheduleOnlyLabelResourceObjectID: PDL.indexOf('MyScheduleOnly_Label_ResourceObjectID'),
    MyScheduleOnlyLabelOperationCode: PDL.indexOf('MyScheduleOnly_Label_OperationCode'),
    MyScheduleOnlyLabelOperationObjectID: PDL.indexOf('MyScheduleOnly_Label_OperationObjectID'),
    MyScheduleOnlyLabelUseInstCode: PDL.indexOf('MyScheduleOnly_Label_UseInstCode'),
    MyScheduleOnlyLabelUseInstObjectID: PDL.indexOf('MyScheduleOnly_Label_UseInstObjectID'),
  };

  data.Operation.sort((a, b) => {
    return a[4].split(";").map((d) => new Date(d))[0] - b[4].split(";").map((d) => new Date(d))[0];
  })
  data.Operation.forEach((uib) => {
    const _taskIdx = uib[_indexes.ObjectID];
    const _opeCode = uib[_indexes.OperationCode];
    const _taskObj = {
      _row: res,
      _res: data.Resource[0],
      _order: uib[_indexes.OrderCode],
      _operation: _opeCode,
      _objectID: _taskIdx,
      _outputItem: uib[_indexes.OutputItem],
      _outputItemQty: uib[_indexes.OutputItemQuantity],
      _color: uib[_indexes.Color],
      _useinstmaster_type: uib[_indexes.UIMType],
      _prevTask: uib[_indexes.PreviousTask],
      _nextTask: uib[_indexes.NextTask],
      _prevTaskLocalLocs: uib[_indexes.PreviousTaskLocalLocs],
      _nextTaskLocalLocs: uib[_indexes.NextTaskLocalLocs],
      _late: (uib[_indexes.Lateness] > 0),
      _early: (uib[_indexes.Earlyness] > 0),
      _productionArr: _SemicolonDateToArray(uib[_indexes.ProductionArray]),
      _resStatus: uib[_indexes.Status],
      _taskText: uib[_indexes.TaskText],
      _hoverTooltip: uib[_indexes.TaskTooltip],
      _setupArr: _SemicolonDateToArray(uib[_indexes.SetupArray]),
      _teardownArr: _SemicolonDateToArray(uib[_indexes.TeardownArray]),
      _resLockArr: _SemicolonDateToArray(uib[_indexes.ResLockArray]),
      _resultStart: uib[_indexes.ResultStartTime],
      _resultEnd: uib[_indexes.ResultEndTime],
      _resultQty: uib[_indexes.ResultQty],
      _timeConstraintMethod: uib[_indexes.TimeConstraintMethod],
      _pegType: uib[_indexes.PegType],
      _procNo: uib[_indexes.ProcNo],
      _procName: uib[_indexes.ProcName],
      _procCode: uib[_indexes.ProcCode],
      _resultProgress: uib[_indexes.ResultProgress],
      _resultObtainTime: uib[_indexes.ResultObtainTime],
      _orderType: uib[_indexes.OrderType],
      _orderItemCode: uib[_indexes.OrderItemCode],
      _orderItemName: uib[_indexes.OrderItemName],
      _takenResourceCount: 1, // Temporary
      _leftTimestamps: (uib[_indexes.ConnectionLineLeftTimestamp] || [] ).map((d) => new Date(d)),
      _rightTimestamps: (uib[_indexes.ConnectionLineRightTimestamps] || []).map((d) => new Date(d)),
      _useInstBarTextColor: uib[_indexes.UseInstBarTextColor]
    };
    AllOperations.push(_taskObj);
  });

  data.Labels.forEach((lb) => {
    const _labelObj = {
      _objectID: lb[_labelIndexes.ObjectID],
      _string: lb[_labelIndexes.LabelString],
      _textHorizontalAlignment: lb[_labelIndexes.LabelTextHorizontalAlignment],
      _textVerticalAlignment: lb[_labelIndexes.LabelTextVerticalAlignment],
      _shape: lb[_labelIndexes.LabelShape],
      _timePoint: lb[_labelIndexes.LabelTimePoint],
      _timeSpan: lb[_labelIndexes.LabelTimeSpan],
      _relYRatio: lb[_labelIndexes.LabelRelYRatio],
      _rectRelX: lb[_labelIndexes.LabelRectRelX],
      _rectRelY: lb[_labelIndexes.LabelRectRelY],
      _z: lb[_labelIndexes.LabelZ],
      _height: lb[_labelIndexes.LabelHeight],
      _width: lb[_labelIndexes.LabelWidth],
      _arrowDepthRatio: lb[_labelIndexes.LabelArrowDepthRatio],
      _arrowStemHeightRatio: lb[_labelIndexes.LabelArrowStemHeightRatio],
      _backColor: lb[_labelIndexes.LabelBackColor],
      _borderColor: lb[_labelIndexes.LabelBorderColor],
      _textColor: lb[_labelIndexes.LabelTextColor],
      _resourceCode: lb[_labelIndexes.MyScheduleOnlyLabelResourceCode],
      _resourceObjectID: lb[_labelIndexes.MyScheduleOnlyLabelResourceObjectID],
      _operationCode: lb[_labelIndexes.MyScheduleOnlyLabelOperationCode],
      _operationObjectID: lb[_labelIndexes.MyScheduleOnlyLabelOperationObjectID],
      _useInstCode: lb[_labelIndexes.MyScheduleOnlyLabelUseInstCode],
      _useInstObjectID: lb[_labelIndexes.MyScheduleOnlyLabelUseInstObjectID],
    };
    AllLabels.push(_labelObj);
  });

  return {
    Data: PostProcessOperations(AllOperations),
    Shift: PostProcessShifts(AllShifts,AllOCalendar),
    Label: AllLabels,
    OCalendar: AllOCalendar,
    IntervalQty: data?.IntervalQty
  };
}

export const FetchOrderInfo = (res, url, token) => {
  const _orderUrl = url;//this.rowDownloadUrl + urlIdx + ".json";
  const xhr = new XMLHttpRequest();
  xhr.open("GET", _orderUrl, false);
  xhr.setRequestHeader('Authorization', 'Bearer ' + token);
  xhr.send();

  if (xhr.status === 200) {
    const data = JSON.parse(xhr.responseText);

    const OrderPDA =  Object.keys(data.PropertyDef_Order);
    let indexes = {
      ObjectID: OrderPDA.indexOf('ObjectID'),
      TaskText: OrderPDA.indexOf('WebViewerOnly_BarStrings'),
      TaskTooltip: OrderPDA.indexOf('WebViewerOnly_DataTipStrings'),
      StartTime: OrderPDA.indexOf('Work_StartTime'),
      EndTime: OrderPDA.indexOf('Work_EndTime'),
      EST:  OrderPDA.indexOf('Order_EST'),
      LET:  OrderPDA.indexOf('Order_LET'),
      BarColors: OrderPDA.indexOf('WebViewerOnly_BarColors'),
      ResultStartTime: OrderPDA.indexOf('Order_ResultStartTime'),
      ResultEndTime: OrderPDA.indexOf('Order_ResultEndTime'),
      StoredStartTime: OrderPDA.indexOf('Order_StoredStartTime'),
      StoredEndTime: OrderPDA.indexOf('Order_StoredEndTime'),
      IsRefOrder : OrderPDA.indexOf('Order_IsRefOrder'),
      Lateness: OrderPDA.indexOf('Work_Lateness'),
      OrderBarRightTextColor: OrderPDA.indexOf('Order_BarRightTextColor'),
    };



    const OperationPDA = Object.keys(data.PropertyDef_Operation);
    const _indexes = {
      ObjectID: OperationPDA.indexOf('ObjectID'),
      OrderCode: OperationPDA.indexOf('WorkInst_Order'),
      OutputItem: OperationPDA.indexOf('Work_OperationOutMainItem'),
      OperationCode: OperationPDA.indexOf('WorkInst_Operation'),
      OutputItemQuantity: OperationPDA.indexOf('Work_OperationOutMainItemQty'),
      // Color: OperationPDA.indexOf('WebViewerOnly_Order_Color_RGB'),
      UIMType: OperationPDA.indexOf('WebViewerOnly_UseInstMaster_Type'),
      PreviousTask: OperationPDA.indexOf('WebViewerOnly_LeftWorkObjectID'),
      NextTask: OperationPDA.indexOf('WebViewerOnly_RightWorkObjectID'),
      PrevOperation: OperationPDA.indexOf('Work_PrevOperation'),
      NextOperation: OperationPDA.indexOf('Work_NextOperation'),
      Lateness: OperationPDA.indexOf('Work_Lateness'),
      ProductionArray: OperationPDA.indexOf('WebViewerOnly_TimeArray_Prod'),
      SetupArray: OperationPDA.indexOf('WebViewerOnly_TimeArray_Setup'),
      TeardownArray: OperationPDA.indexOf('WebViewerOnly_TimeArray_Teardown'),
      Status: OperationPDA.indexOf('Work_Status'),
      TaskText: OperationPDA.indexOf('WebViewerOnly_BarStrings'),
      TaskTooltip: OperationPDA.indexOf('WebViewerOnly_DataTipStrings'),
      ResultStartTime: OperationPDA.indexOf('Work_ResultStartTime'),
      ResultEndTime: OperationPDA.indexOf('Work_ResultEndTime'),
      ResultQty: OperationPDA.indexOf('Work_ResultQty'),
      BarColors: OperationPDA.indexOf('WebViewerOnly_BarColors'),
      ResultProgress: OperationPDA.indexOf('Work_ResultProgress'),
      ResultObtainTime: OperationPDA.indexOf('Work_ResultObtainTime'),
      OperationBarInsideText: OperationPDA.indexOf('Work_OperationBarInsideText'),
      OperationBarInsideTextColor: OperationPDA.indexOf('Work_OperationBarInsideTextColor'),
      OperationBarRightTextColor: OperationPDA.indexOf('Work_OperationBarRightTextColor'),
    };

    let AllOrders = [];
    let AllOperations = [];
    let AllPeggedOrders = [];

    for (let i = 0; i < data.Order.length; i++) {
      let ord = data.Order[i];
      const ordObj = {
        _objectID: ord[indexes.ObjectID],
        _taskText: ord[indexes.TaskText],
        _hoverTooltip: ord[indexes.TaskTooltip],
        _est: ord[indexes.EST] ? new Date(ord[indexes.EST]) : null,
        _let: ord[indexes.LET] ? new Date(ord[indexes.LET]) : null,
        _late: (ord[indexes.Lateness] > 0),
        _productionArr: [new Date(ord[indexes.StartTime]), new Date(ord[indexes.EndTime])],
        _barColors: ord[indexes.BarColors],
        _resultStart: ord[indexes.ResultStartTime] ? new Date(ord[indexes.ResultStartTime]) : null,
        _resultEnd: ord[indexes.ResultEndTime] ? new Date(ord[indexes.ResultEndTime]) : null,
        _storedStartTime:  ord[indexes.StoredStartTime] ? new Date(ord[indexes.StoredStartTime]) : null,
        _storedEndTime:  ord[indexes.StoredEndTime] ? new Date(ord[indexes.StoredEndTime]) : null,
        _isRefOrder: ord[indexes.IsRefOrder] === 1 ? true : false,
        _orderBarRightTextColor: ord[indexes.OrderBarRightTextColor]
      }
      AllOrders.push(ordObj);
    }

    // data.Operation.sort((a, b) => {
    //   return a[4].split(";").map((d) => new Date(d))[0] - b[4].split(";").map((d) => new Date(d))[0];
    // })

    data.Operation.forEach((uib) => {
      const _taskIdx = uib[_indexes.ObjectID];
      const _opeCode = uib[_indexes.OperationCode];
      const _taskObj = {
        _row: res,
        _order: uib[_indexes.OrderCode],
        _operation: _opeCode,
        _objectID: _taskIdx,
        _outputItem: uib[_indexes.OutputItem],
        _outputItemQty: uib[_indexes.OutputItemQuantity],
        // _color: uib[_indexes.Color],
        _useinstmaster_type: uib[_indexes.UIMType],
        _prevTask: uib[_indexes.PreviousTask],
        _nextTask: uib[_indexes.NextTask],
        _prevOpe: uib[_indexes.PrevOperation],
        _nextOpe: uib[_indexes.NextOperation],
        _late: (uib[_indexes.Lateness] > 0),
        _productionArr: _SemicolonDateToArray(uib[_indexes.ProductionArray]),
        _resStatus: uib[_indexes.Status],
        _taskText: uib[_indexes.TaskText],
        _hoverTooltip: uib[_indexes.TaskTooltip],
        _setupArr: _SemicolonDateToArray(uib[_indexes.SetupArray]),
        _teardownArr: _SemicolonDateToArray(uib[_indexes.TeardownArray]),
        _resultStart: uib[_indexes.ResultStartTime],
        _resultEnd: uib[_indexes.ResultEndTime],
        _resultQty: uib[_indexes.ResultQty] ? new Date(uib[_indexes.ResultQty]) : null,
        _barColors: uib[_indexes.BarColors],
        _resultProgress: uib[_indexes.ResultProgress],
        _resultObtainTime: uib[_indexes.ResultObtainTime],
        _operationBarInsideText: uib[_indexes.OperationBarInsideText],
        _operationBarInsideTextColor: uib[_indexes.OperationBarInsideTextColor],
        _operationBarRightTextColor: uib[_indexes.OperationBarRightTextColor]
      };
      AllOperations.push(_taskObj);
    });

    if (data.PeggedOrders) {
      AllPeggedOrders = data.PeggedOrders.map(dataPerStyle => {
        let dump = Array();
        dataPerStyle.forEach((uib) => {
          const _taskIdx = uib[_indexes.ObjectID];
          const _opeCode = uib[_indexes.OperationCode];
          const _taskObj = {
            _row: res,
            _order: uib[_indexes.OrderCode],
            _operation: _opeCode,
            _objectID: _taskIdx,
            _outputItem: uib[_indexes.OutputItem],
            _outputItemQty: uib[_indexes.OutputItemQuantity],
            // _color: uib[_indexes.Color],
            _useinstmaster_type: uib[_indexes.UIMType],
            _prevTask: uib[_indexes.PreviousTask],
            _nextTask: uib[_indexes.NextTask],
            _prevOpe: uib[_indexes.PrevOperation],
            _nextOpe: uib[_indexes.NextOperation],
            _late: (uib[_indexes.Lateness] > 0),
            _productionArr: _SemicolonDateToArray(uib[_indexes.ProductionArray]),
            _resStatus: uib[_indexes.Status],
            _taskText: uib[_indexes.TaskText],
            _hoverTooltip: uib[_indexes.TaskTooltip],
            _setupArr: _SemicolonDateToArray(uib[_indexes.SetupArray]),
            _teardownArr: _SemicolonDateToArray(uib[_indexes.TeardownArray]),
            _resultStart: uib[_indexes.ResultStartTime],
            _resultEnd: uib[_indexes.ResultEndTime],
            _resultQty: uib[_indexes.ResultQty] ? new Date(uib[_indexes.ResultQty]) : null,
            _barColors: uib[_indexes.BarColors],
            _resultProgress: uib[_indexes.ResultProgress],
            _resultObtainTime: uib[_indexes.ResultObtainTime]
          };
          dump.push(_taskObj);
        });
        return dump;
      });

    }


    return {
      // Data: {"Order": ordObj, "Operation": AllOperations},
      // Data: AllOperations
      // Data: AllOrders
      OrderData: AllOrders,
      Data: AllOperations,
      PeggedOrderData: AllPeggedOrders
    };
  }
  console.error("Failed to fetch : " + res);
  return {
    OrderData: [],
    Data: []
  };
}

const FetchTransactionData = (url, timestamp) => {
  try {
    console.log("timestamp: " + timestamp);
    let _trUrl = url;
    if (timestamp)
      _trUrl += "?timestamp=" + timestamp;
    const xhr = new XMLHttpRequest();
    xhr.open("GET", _trUrl, false);
    xhr.send();

    if (xhr.status === 200) {
      const data = JSON.parse(xhr.responseText);

      return data;
    }
    else {
      console.error("Failed to fetch transactions");
      return null;
    }
  }
  catch {
    console.error("Failed to fetch transactions");
    return null;
  }
}

const FetchTransactionDataAsync = (url, timestamp, success_callback, error_callback) => {
  try {
    console.log("timestamp: " + timestamp);
    let _trUrl = url;
    if (timestamp)
      _trUrl += "?timestamp=" + timestamp;

    const xhr = new XMLHttpRequest();
    xhr.open("GET", _trUrl, true);
    xhr.timeout = 2000;
    xhr.onload = () => {
      if (xhr.readyState === 4) {
        if (xhr.status === 200) {
          const data = JSON.parse(xhr.responseText);
          success_callback(data);
        }
        else {
          console.error("Failed to fetch transactions");
          error_callback();
        }
      }
    };
    xhr.ontimeout = (e) => {
      console.error("Connection to server timed out");
      error_callback();
    };
    xhr.onerror = (e) => {
      console.error("Failed to fetch transactions");
      error_callback();
    };

    xhr.send(null);


  }
  catch {
    console.error("Failed to fetch transactions");
    error_callback();
  }
}

const FetchMetadataAsync = (url, success_callback, error_callback) => {
  try {
    const xhr = new XMLHttpRequest();
    xhr.open("GET", url, true);
    xhr.timeout = 2000;
    xhr.onload = () => {
      if (xhr.readyState === 4) {
        if (xhr.status === 200) {
          const data = JSON.parse(xhr.responseText);
          success_callback(data);
        }
        else {
          console.error("Failed to fetch meta.json");
          error_callback();
        }
      }
    };
    xhr.ontimeout = (e) => {
      console.error("Connection to server timed out");
      error_callback();
    };
    xhr.onerror = (e) => {
      console.error("Failed to fetch meta.json");
      error_callback();
    };
    xhr.send(null);
  }
  catch {
    console.error("Failed to fetch meta.json");
    error_callback();
  }
}

export default {
  FetchResourceInfo,
  FetchOrderInfo,
  FetchTransactionData,
  FetchTransactionDataAsync,
  FetchMetadataAsync,
  FetchLocs,
}
